/**
 * Gmail integration card — interactive tile that lives inside SecurityPanel.
 *
 * Contract:
 *   - GET  /api/integrations/gmail/status      → shows connected / not connected
 *   - GET  /api/integrations/gmail/connect     → returns {authorization_url}, we
 *                                                navigate the user to Google.
 *   - POST /api/integrations/gmail/disconnect  → revokes + deletes token.
 *
 * Never constructs the OAuth URL client-side. Never exposes tokens, secrets
 * or scopes beyond what /status returns.
 */
import { useCallback, useEffect, useState } from 'react';
import { Mail, Check, CircleSlash, Loader2, LogOut, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reuse the same localStorage key App.js sets after login so cross-origin
// deployments (alivan.co.il + backend) survive without cookies.
const AUTH_TOKEN_KEY = 'alivan-auth-token';
const authHeaders = () => {
  try {
    const t = localStorage.getItem(AUTH_TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch (_e) {
    return {};
  }
};

const GmailIntegrationCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/integrations/gmail/status`, {
        credentials: 'include',
        headers: { ...authHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStatus(data);
    } catch (e) {
      setError('No se pudo consultar el estado de Gmail.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Detect ?gmail=connected on the URL, refresh status, strip the param.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail') === 'connected') {
      toast.success('Gmail conectado', {
        description: 'AliVan ya puede leer tu Gmail y preparar borradores.',
      });
      params.delete('gmail');
      const q = params.toString();
      const clean = window.location.pathname + (q ? `?${q}` : '') + window.location.hash;
      window.history.replaceState(null, '', clean);
      load();
    } else if (params.get('gmail') === 'error') {
      toast.error('No pudimos conectar Gmail. Vuelve a intentarlo.');
      params.delete('gmail');
      const q = params.toString();
      const clean = window.location.pathname + (q ? `?${q}` : '') + window.location.hash;
      window.history.replaceState(null, '', clean);
      load();
    }
  }, [load]);

  const onConnect = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/integrations/gmail/connect`, {
        credentials: 'include',
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${detail.slice(0, 120)}`);
      }
      const data = await res.json();
      if (!data.authorization_url) {
        throw new Error('Respuesta sin authorization_url');
      }
      // Top-level navigation so Google can set its session cookies.
      window.location.assign(data.authorization_url);
    } catch (e) {
      setError('No se pudo iniciar el flujo de Gmail. Reintenta en unos segundos.');
      setBusy(false);
    }
  };

  const onDisconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/integrations/gmail/disconnect`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      toast.success('Gmail desconectado');
      await load();
    } catch (e) {
      setError('No se pudo desconectar Gmail.');
    } finally {
      setBusy(false);
    }
  };

  const available = status?.available !== false;
  const connected = !!status?.connected;
  const accountEmail = status?.account_email || null;

  return (
    <section data-testid="gmail-integration-card" className="p-4 border border-white/15 bg-black/40 backdrop-blur-xl">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <span
            className={`mt-1 w-9 h-9 flex items-center justify-center border ${
              connected ? 'border-emerald-400/50 bg-emerald-500/10' : 'border-white/15 bg-black/40'
            } flex-shrink-0`}
          >
            <Mail className={`w-4 h-4 ${connected ? 'text-emerald-300' : 'text-white/70'}`} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-outfit text-sm text-white" data-testid="gmail-label">
                Gmail
              </span>
              {loading ? (
                <span className="inline-flex items-center gap-1 font-jetbrains text-[10px] tracking-widest uppercase text-white/40">
                  <Loader2 className="w-3 h-3 animate-spin" /> comprobando
                </span>
              ) : !available ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 border border-yellow-400/40 text-yellow-300 font-jetbrains text-[10px] tracking-widest uppercase"
                  data-testid="gmail-status-badge"
                >
                  <AlertTriangle className="w-3 h-3" /> Configuración pendiente
                </span>
              ) : connected ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 border border-emerald-400/40 text-emerald-300 font-jetbrains text-[10px] tracking-widest uppercase"
                  data-testid="gmail-status-badge"
                >
                  <Check className="w-3 h-3" /> Conectado
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 border border-white/15 text-white/50 font-jetbrains text-[10px] tracking-widest uppercase"
                  data-testid="gmail-status-badge"
                >
                  <CircleSlash className="w-3 h-3" /> No conectado
                </span>
              )}
            </div>
            {connected && accountEmail && (
              <p
                className="font-jetbrains text-[11px] text-white/60 mt-1 truncate"
                data-testid="gmail-account-email"
              >
                {accountEmail}
              </p>
            )}
            {!connected && available && !loading && (
              <p className="font-jetbrains text-[11px] text-white/50 mt-1">
                Lectura + creación de borradores. Nunca envío automático.
              </p>
            )}
            {!available && status?.reason && (
              <p className="font-jetbrains text-[10px] text-yellow-300/80 mt-1">
                {status.reason}
              </p>
            )}
            {error && (
              <p className="font-jetbrains text-[10px] text-neon-red mt-1" data-testid="gmail-error">
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="flex-shrink-0">
          {connected ? (
            <button
              onClick={onDisconnect}
              disabled={busy}
              data-testid="gmail-disconnect-button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neon-red/40 text-neon-red hover:bg-neon-red/10 font-jetbrains text-[11px] tracking-widest uppercase transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
              Desconectar
            </button>
          ) : (
            <button
              onClick={onConnect}
              disabled={busy || loading || !available}
              data-testid="gmail-connect-button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 font-jetbrains text-[11px] tracking-widest uppercase transition-colors disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
              Conectar Gmail
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default GmailIntegrationCard;
